// Parses `git show <sha>` / `git diff` plain text (vfs-service GET /history/:sha/diff, vfsClient.getCommitDiff) for
// read-only rendering: the commit header (sha, author, date, message, trailers such as Via: / AI-Model:) and, per
// file, its paths, status (added / deleted / renamed / modified / binary) and hunks with old/new line numbers.
// Not src/utils/diffParser.js: that one parses diffs to APPLY them (fuzzy hunk matching), a different job.

const TRAILER_RE = /^([A-Za-z][A-Za-z0-9-]*):\s+(.+)$/;

/** `a/x` / `b/x` / `"a/x y"` → `x`; `/dev/null` → null. */
function stripPrefix(raw) {
  if (raw == null) { return null; }
  let value = raw.trim();
  if (value.startsWith('"') && value.endsWith('"')) { value = value.slice(1, -1); }
  if (value === '/dev/null') { return null; }
  return value.replace(/^[ab]\//, '');
}

// `diff --git a/x b/x` (paths may contain spaces; when old == new the line is symmetric, which is the common case).
function pathsOfDiffGit(rest) {
  const quoted = rest.match(/^"(a\/.*)" "(b\/.*)"$/);
  if (quoted) { return [stripPrefix(quoted[1]), stripPrefix(quoted[2])]; }
  const half = (rest.length - 1) / 2;
  if (Number.isInteger(half) && rest.slice(0, 2) === 'a/' && rest.slice(half + 1, half + 3) === 'b/' && rest.slice(2, half) === rest.slice(half + 3)) {
    return [rest.slice(2, half), rest.slice(half + 3)];
  }
  const match = rest.match(/^a\/(.+) b\/(.+)$/);
  return match ? [match[1], match[2]] : [rest, rest];
}

function newFile(oldPath, newPath) {
  return { oldPath, newPath, path: newPath || oldPath, status: 'modified', binary: false, similarity: null, mode: null, hunks: [], additions: 0, deletions: 0 };
}

/** The message lines (4-space indented in `git show`) → subject, body, trailers (the last paragraph of `Key: value` lines). */
function parseMessage(lines) {
  const text = lines.map((l) => (l.startsWith('    ') ? l.slice(4) : l.trim()));
  while (text.length && !text[0].trim()) { text.shift(); }
  while (text.length && !text[text.length - 1].trim()) { text.pop(); }
  const paragraphs = [];
  let current = [];
  for (const line of text) {
    if (!line.trim()) {
      if (current.length) { paragraphs.push(current); current = []; }
    } else {
      current.push(line);
    }
  }
  if (current.length) { paragraphs.push(current); }
  let trailers = [];
  if (paragraphs.length > 1 && paragraphs[paragraphs.length - 1].every((l) => TRAILER_RE.test(l))) {
    trailers = paragraphs.pop().map((l) => { const m = l.match(TRAILER_RE); return { key: m[1], value: m[2].trim() }; });
  }
  const subject = paragraphs.length ? paragraphs[0].join(' ') : '';
  const body = paragraphs.slice(1).map((p) => p.join('\n')).join('\n\n');
  return { subject, body, trailers };
}

/**
 * `{ commit, files }`. `commit` is null when the text has no `commit <sha>` header (a plain `git diff`), else
 * `{ sha, author, authorEmail, date, subject, body, trailers: [{key, value}] }`. Each file: `{ oldPath, newPath,
 * path, status, binary, similarity, mode, additions, deletions, hunks: [{ header, oldStart, newStart, section, rows }] }`,
 * a row `{ type: 'add'|'del'|'ctx', oldLineNum?, newLineNum?, text, noNewline? }`.
 */
export function parseCommitDiff(diffText) {
  const lines = (diffText || '').split('\n');
  const files = [];
  let commit = null;
  const messageLines = [];
  let file = null;
  let hunk = null;
  let oldLine = 0;
  let newLine = 0;
  let oldLeft = 0;
  let newLeft = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (line.startsWith('diff --git ')) {
      const [oldPath, newPath] = pathsOfDiffGit(line.slice('diff --git '.length));
      file = newFile(oldPath, newPath);
      files.push(file);
      hunk = null;
      continue;
    }
    if (!file) {
      // The commit header, before the first file.
      const sha = line.match(/^commit ([0-9a-f]{7,64})\b/);
      if (sha && !commit) {
        commit = { sha: sha[1], author: '', authorEmail: '', date: '', subject: '', body: '', trailers: [] };
        continue;
      }
      if (commit) {
        const author = line.match(/^Author:\s+(.*?)\s*(?:<([^>]*)>)?\s*$/);
        if (author && !messageLines.length) { commit.author = author[1]; commit.authorEmail = author[2] || ''; continue; }
        const date = line.match(/^(?:Date|AuthorDate):\s+(.*)$/);
        if (date && !messageLines.length) { commit.date = date[1].trim(); continue; }
        if (/^(Merge|Commit|CommitDate):\s/.test(line) && !messageLines.length) { continue; }
        messageLines.push(line);
      }
      continue;
    }
    // Inside a hunk, the counts say how many lines belong to it — so a content line that happens to start with
    // `--- ` is not mistaken for a header. Past the counts (a hand-written patch with wrong ones) +/-/space lines still
    // count, all but the `--- ` / `+++ ` of a next file.
    const counted = oldLeft > 0 || newLeft > 0;
    if (hunk && (counted || (/^[-+ ]/.test(line) && !/^(--- |\+\+\+ )/.test(line)))) {
      if (line.startsWith('+')) {
        hunk.rows.push({ type: 'add', newLineNum: newLine, text: line.slice(1) });
        newLine += 1; newLeft -= 1; file.additions += 1;
        continue;
      }
      if (line.startsWith('-')) {
        hunk.rows.push({ type: 'del', oldLineNum: oldLine, text: line.slice(1) });
        oldLine += 1; oldLeft -= 1; file.deletions += 1;
        continue;
      }
      if (line.startsWith(' ') || (line === '' && counted)) {
        // An empty line is a context line whose leading space was trimmed (but not the text's trailing newline).
        if (line === '' && i === lines.length - 1) { continue; }
        hunk.rows.push({ type: 'ctx', oldLineNum: oldLine, newLineNum: newLine, text: line.slice(1) });
        oldLine += 1; newLine += 1; oldLeft -= 1; newLeft -= 1;
        continue;
      }
    }
    if (line.startsWith('\\')) {
      // "\ No newline at end of file": belongs to the row above, not a line of its own.
      const last = hunk && hunk.rows[hunk.rows.length - 1];
      if (last) { last.noNewline = true; }
      continue;
    }
    const hunkMatch = line.match(/^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@ ?(.*)$/);
    if (hunkMatch) {
      oldLine = parseInt(hunkMatch[1], 10);
      newLine = parseInt(hunkMatch[3], 10);
      oldLeft = hunkMatch[2] === undefined ? 1 : parseInt(hunkMatch[2], 10);
      newLeft = hunkMatch[4] === undefined ? 1 : parseInt(hunkMatch[4], 10);
      hunk = { header: line, oldStart: oldLine, newStart: newLine, section: hunkMatch[5] || '', rows: [] };
      file.hunks.push(hunk);
      continue;
    }
    if (line.startsWith('new file mode')) { file.status = 'added'; file.mode = line.split(' ').pop(); continue; }
    if (line.startsWith('deleted file mode')) { file.status = 'deleted'; file.mode = line.split(' ').pop(); continue; }
    if (line.startsWith('rename from ')) { file.oldPath = line.slice('rename from '.length); file.status = 'renamed'; continue; }
    if (line.startsWith('rename to ')) { file.newPath = line.slice('rename to '.length); file.path = file.newPath; file.status = 'renamed'; continue; }
    if (line.startsWith('copy from ')) { file.oldPath = line.slice('copy from '.length); file.status = 'copied'; continue; }
    if (line.startsWith('copy to ')) { file.newPath = line.slice('copy to '.length); file.path = file.newPath; file.status = 'copied'; continue; }
    if (line.startsWith('similarity index ')) { file.similarity = parseInt(line.slice('similarity index '.length), 10); continue; }
    if (line.startsWith('Binary files ') || line === 'GIT binary patch') { file.binary = true; continue; }
    if (line.startsWith('--- ')) {
      const path = stripPrefix(line.slice(4));
      if (path === null) { file.status = 'added'; } else if (file.status !== 'renamed' && file.status !== 'copied') { file.oldPath = path; }
      continue;
    }
    if (line.startsWith('+++ ')) {
      const path = stripPrefix(line.slice(4));
      if (path === null) { file.status = 'deleted'; } else if (file.status !== 'renamed' && file.status !== 'copied') { file.newPath = path; file.path = path; }
      continue;
    }
    // index …, old mode / new mode, dissimilarity index…: nothing to show.
  }

  if (commit) { Object.assign(commit, parseMessage(messageLines)); }
  return { commit, files };
}

/** The files only (UnifiedDiffView's input). */
export function parseUnifiedDiff(diffText) {
  return parseCommitDiff(diffText).files;
}

/** The value of a trailer (case-insensitive key), or null. */
export function trailerValue(commit, key) {
  const wanted = String(key).toLowerCase();
  const found = (commit?.trailers || []).find((t) => t.key.toLowerCase() === wanted);
  return found ? found.value : null;
}

const WORD_CASE = { ai: 'AI', gpt: 'GPT', llm: 'LLM', api: 'API' };

/** `google-vertex/gemini-2.5-pro` → `Gemini 2.5 Pro`; an unknown-shaped id is kept as it is. */
export function prettyModel(raw) {
  if (!raw) { return ''; }
  const model = String(raw).split('/').pop();
  if (!/^[a-z0-9.-]+$/i.test(model)) { return model; }
  return model
    .split('-')
    .filter(Boolean)
    .map((part) => WORD_CASE[part.toLowerCase()] || (/^[a-z]/.test(part) ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

/**
 * The `sub` of a vfs-service author (`user:<sub>`, also `user:user:<sub>` as `git show` prints a person's or an agent
 * driven for them), or null for an agent working for nobody / an unknown shape.
 */
export function subOfAuthor(author) {
  let value = String(author || '').trim();
  if (!value.startsWith('user:')) { return null; }
  while (value.startsWith('user:')) { value = value.slice(5); }
  return value || null;
}
